import type { FacilityNode } from "@arc/contracts";
import type { FacilityTreeNode } from "../facility-tree.js";
import { FacilityRailCard } from "./FacilityRailCard.js";
import type { KebabProps } from "./useDrilldownActions.js";
import { FacilityIcon } from "@/components/icons";
import { cn } from "@/lib/utils";

/**
 * The rail/flat list of facility cards (FN / ADR-014). A semantic `<ul>`/`<li>` (not `role="tree"`): a one-level list,
 * not an expandable tree. Two modes, selected by whether `getPathLabel` is supplied:
 * - **rail** (default) — timeline cards for one level (the roots, or a drilled-into facility's direct children);
 * - **flat** (`getPathLabel` set) — no timeline; each card shows its ancestor **path breadcrumb**, for the global
 *   Favourites / Archived lists (FN4a) and the search results (FN4b). `navigable={false}` drops the ↘/→ nav (archived).
 */
export function FacilityRailList({
  nodes,
  ariaLabel,
  emptyLine,
  getKebab,
  favorites,
  onToggleFavorite,
  onDrill,
  onOpenManuals,
  getPathLabel,
  navigable = true,
  archived = false,
  showFavorite = true,
  getUnarchive,
  timeline = false,
  selectedId = null,
  onSelect,
}: {
  nodes: readonly FacilityTreeNode[];
  ariaLabel: string;
  emptyLine: string;
  /** Build the management-kebab props for a node (null ⇒ the caller can't act → no kebab). */
  getKebab: (node: FacilityNode) => KebabProps | null;
  favorites: ReadonlySet<number>;
  onToggleFavorite: (id: number) => void;
  onDrill: (id: number) => void;
  onOpenManuals: (id: number) => void;
  /** When set, render the FLAT variant: each card gets this ancestor-path breadcrumb (empty string ⇒ a root, no path). */
  getPathLabel?: (node: FacilityTreeNode) => string;
  /** Passed through to each card — `false` hides the ↘/→ nav controls (archived facilities). */
  navigable?: boolean;
  /** Restore-only mode (the Archived list) — cards hide star/kebab/nav, showing only an Unarchive button. */
  archived?: boolean;
  /** Show the favourite star on each card. `false` for the shop-floor kiosk (ADR-036) — a shared account. */
  showFavorite?: boolean;
  /** Unarchive handler for a node, or null when it isn't an archived-subtree ROOT (no button — restore the parent). */
  getUnarchive?: (node: FacilityTreeNode) => (() => void) | null;
  /**
   * Draw the timeline rail (a drill level's children): a vertical line that starts at the level HEADER above (it reaches
   * up through the 12px gap) and ends at the last card's dot — the rail means "these hang under that". The root list
   * has none (it carried no information there).
   */
  timeline?: boolean;
  /** The card shown in the preview panel (orange frame). */
  selectedId?: number | null;
  /** Make cards selectable for the preview panel (wide screens only). */
  onSelect?: (id: number) => void;
}) {
  if (nodes.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 py-16 text-center">
        <FacilityIcon aria-hidden="true" className="size-8 opacity-40" />
        <p className="text-sm">{emptyLine}</p>
      </div>
    );
  }
  return (
    <ul
      aria-label={ariaLabel}
      className={cn(
        // Spacing via `gap`, not a per-card margin: the first card's top edge must sit flush with the list's top so it
        // lines up with the preview panel beside it (owner 2026-10-22). The timeline keeps its old 5px lead-in below
        // the level header (`pt-[5px]`); its rail ends at the last card's centre (min-h 66 ⇒ 33px).
        "rail-reveal m-0 flex list-none flex-col gap-2.5 p-0",
        timeline &&
          "relative pt-[5px] pl-7 before:absolute before:-top-3 before:bottom-[33px] before:left-2 before:w-0.5 before:bg-input before:content-[''] sm:pl-10 sm:before:left-5",
      )}
    >
      {nodes.map((node, index) => (
        <FacilityRailCard
          key={node.id}
          node={node}
          index={index}
          kebab={getKebab(node)}
          isFavorite={favorites.has(node.id)}
          onToggleFavorite={onToggleFavorite}
          onDrill={onDrill}
          onOpenManuals={onOpenManuals}
          pathLabel={getPathLabel ? getPathLabel(node) : undefined}
          navigable={navigable}
          archived={archived}
          showFavorite={showFavorite}
          onUnarchive={getUnarchive ? (getUnarchive(node) ?? undefined) : undefined}
          timeline={timeline}
          selected={selectedId === node.id}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
}
